import { computed, ref } from '@vue/composition-api'
import { getConfigKeys } from '../utils/api/config-keys.js'

const configKeysState = ref(null)
// Bumped on every load() failure. Read (see `configKeys` below) purely to be a Vue reactive
// dependency: a failed load leaves configKeysState.value unchanged (still null), so without some
// OTHER ref changing, Vue's computed caching means `configKeys` never re-runs its body and thus
// never calls load() again - the failure would be replayed forever. Bumping this ref is what
// invalidates that cache (AB#26561 PR #2723 review).
const failureCount = ref(0)

let isLoading = false
let isLoadingPromise = null
let isReloading = false
const load = async () => {
	if (isLoading) return
	isLoading = true
	// isLoadingPromise is also what waitForLoad() hands its callers - they only want to know the
	// load has settled, never an unhandled rejection to handle themselves. So this ALWAYS
	// resolves; failureCount above (not a rejection here) is the real signal a `configKeys`
	// consumer sees on failure.
	isLoadingPromise = getConfigKeys()
		.then(configKeysData => {
			configKeysState.value = configKeysData.data
		})
		.catch(error => {
			// A failed fetch (e.g. a pre-auth 401) must not wedge the singleton: leave
			// configKeysState.value at its current value (null on a first-load failure) so the
			// `configKeys` computed keeps returning a safe empty-array fallback. Logged once here
			// since this rejection is deliberately swallowed rather than surfaced to callers.
			console.error('config-keys: failed to load config keys', error)
			failureCount.value++
		})
	try {
		await isLoadingPromise
	} finally {
		isLoading = false
	}
}

export const waitForLoad = async () => {
	if (isLoadingPromise === null) load()
	return isLoadingPromise
}
export const reloadConfigKeys = async () => {
	if (isReloading) return
	isReloading = true
	try {
		const { data } = await getConfigKeys()
		configKeysState.value = data
	} finally {
		isReloading = false
	}
}

export const configKeys = computed(() => {
	// failureCount.value >= 0 is always true - it's read here only so this computed depends on
	// it (see the ref's own comment for why that's needed for retry to actually happen).
	if (configKeysState.value === null && failureCount.value >= 0) {
		load()
		// Every sibling computed below reads this via `configKeys.value?.find(...)` - `?.` only
		// guards against configKeys.value itself being null/undefined, not against it being some
		// other truthy non-array shape, so this fallback must already be an array (an empty
		// object here would throw "configKeys.value.find is not a function" the moment any
		// consumer renders while a load is still pending or has failed).
		return []
	}
	return configKeysState.value
})

export const adminPortalBrandFieldLabel = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'AdminPortalBrandLabel')?.keyValue)
export const noOfAllowedExtendedPrefKeys = computed(() => Number(configKeys.value?.find(({ keyName }) => keyName === 'NoOfAllowedExtendedPrefKeys')?.keyValue))
export const licenseId = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'LicenseID')?.keyValue)
export const senderEmailsIdentityLimit = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'SenderEmailsIdentityLimit')?.keyValue)
export const zohoNewTicketLink = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'ZohoNewTicketLink')?.keyValue)
export const zohoViewTicketLink = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'ZohoViewTicketsLink')?.keyValue)
export const learningHubLink = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'LearningHubLink')?.keyValue)
const statementDrafts = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'StatementDrafts')?.keyValue?.toLowerCase())
const cassieIdentityService = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'IsCISVisibleInAdminPortal')?.keyValue)
const enableLegacyPermissionsInVue = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'EnableLegacyPermissionsInVuePortal')?.keyValue?.toLowerCase())
export const activeExportsLimit = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'ExportsEnabled')?.keyValue)
export const exportsLatestFileSizeLimit = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'ExportsLatestFileSizeLimit')?.keyValue)
export const cookieBannerLegacyEmbedCodeVueDisplay = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'CookieBannerLegacyEmbedCodeVueDisplay')?.keyValue.toLowerCase() === 'true')
export const enableCassieCloudEmbedCode = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'EnableCassieCloudEmbedCode')?.keyValue.toLowerCase() === 'true')
export const enableHelpAssistant = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'EnableHelpAssistant')?.keyValue?.toLowerCase() === 'true')
export const enableTransactionTypes = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'EnableTransactionTypes')?.keyValue.toLowerCase() === 'true')
export const enableConsentCollectionPreferencePageOptions = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'ConsentCollectionPreferencePageOptions')?.keyValue)
export const enableConsentCollectionWidgetOptions = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'ConsentCollectionWidgetOptions')?.keyValue)
export const submissionFragmentsEnabled = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'SubmissionFragmentsEnabled')?.keyValue.toLowerCase() === 'true')
// DashboardModuleEnabled gates the "Dashboard" nav item under Cookies in the admin portal sidebar.
// It is intentionally NOT used for product-switcher visibility — see nextGenBannerOption/nextGenBannerEnabled for that.
export const dashboardModuleEnabled = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'DashboardModuleEnabled')?.keyValue?.toLowerCase() === 'true')
// NextGenBannerProductSwitcherOptions: 0 = legacy only, 1 = next-gen only, 2 = both.
// Numeric value (may arrive as a string from the API).
export const nextGenBannerOption = computed(() => {
	const raw = configKeys.value?.find(({ keyName }) => keyName === 'NextGenBannerProductSwitcherOptions')?.keyValue
	if (raw == null) return 0
	const num = Number(raw)
	return num === 0 || num === 1 || num === 2 ? num : 0
})
// Convenience boolean: true when the next-gen consent banners card should appear (option 1 or 2).
export const nextGenBannerEnabled = computed(() => nextGenBannerOption.value >= 1)
export const mobileConsentEnabled = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'MobileConsentModuleEnabled')?.keyValue?.toLowerCase() === 'true')
export const privacyNoticeModuleEnabled = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'PrivacyNoticeModuleEnabled')?.keyValue?.toLowerCase() === 'true')
export const prefPageAnalyticsEnabled = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'PrefPageAnalyticsEnabled')?.keyValue?.toLowerCase() === 'true')
export const consentConfigApiEnabled = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'ConsentConfigApiEnabled')?.keyValue?.toLowerCase() === 'true')
export const addAdminPortalUserIfNotExist = computed(() =>
	configKeys.value
		?.find(({ keyName }) => keyName === 'AddAdminPortalUserIfNotExist')
		?.keyValue.toLowerCase() === 'true'
)

export const addCSPortalUserIfNotExist = computed(() =>
	configKeys.value
		?.find(({ keyName }) => keyName === 'AddCSUserIfNotExist')
		?.keyValue.toLowerCase() === 'true'
)

export const ssoCspUserIdentityField = computed(() => {
	return configKeys.value
		?.find(({ keyName }) => keyName === 'SSOCsUserIdentityField')
		?.keyValue
})
export const showStatementDrafts = computed(() => {
	return statementDrafts.value === 'true'
})

export const showLegacyPermissions = computed(() => {
	return enableLegacyPermissionsInVue.value === 'true'
})

export const siteScannerVersion = computed(() => Number(configKeys.value?.find(({ keyName }) => keyName === 'SiteScannerVersion')?.keyValue))
export const siteScannerModuleEnabled = computed(() => configKeys.value?.find(({ keyName }) => keyName === 'SiteScannerModuleEnabled')?.keyValue?.toLowerCase() === 'true')

export const cassieIdentityServiceEnabled = computed(() => {
	return cassieIdentityService.value === '1'
})
